/**
 * The ticket table, shared by /tickets and /review (P22).
 *
 * Extracted from `TicketBoard.tsx` so the two pages cannot drift: a reviewer
 * who learns the board on one page has learned it on both. The pages differ in
 * exactly two things — which rows the API is asked for, and whether the review
 * reason gets a column — and those are props, not copies.
 *
 * Every status and reason shown here goes through `lib/labels.ts`. The raw
 * code is still reachable: it is the `title` of the badge, so the one person
 * who needs `AuthCustomerUnresolved` verbatim (grepping the audit trail) can
 * hover for it. Since P30 the COLUMN NAMES come from there too — five surfaces
 * now name the same column and they must all say the same word.
 *
 * =========================================================================
 * THE TABLE TOOLBOX (P30) — AND THE ONE THING THAT CAN LIE
 * =========================================================================
 * Every header carries a funnel menu. There are two completely different
 * mechanisms behind those menus and the distinction is the whole design:
 *
 *   SERVER COLUMNS — Status, Company, Lang, Owner, Created and (on /review)
 *     "What is being asked". Ticking a box writes the endpoint's own query
 *     parameter, the SQL re-runs, and the answer is complete by construction.
 *     Their value lists are CANONICAL (`labels.ts`, the pilot's languages, the
 *     real assignees), never derived from the loaded rows — a value derived
 *     from rows the server already filtered out could never be ticked back on.
 *
 *   CLIENT COLUMNS — Ticket, Customer, Product, Priority, Doc Type,
 *     Authorization, Due. These filter the rows the browser holds, Excel-style,
 *     with each column's value list narrowed by the OTHER columns' filters.
 *     They are honest only as far as the page is complete, which is why the
 *     endpoint reports the total and the strip above says "500 of N loaded"
 *     the moment the two differ.
 *
 * Customer and Product cannot be server columns at all: `attachTicketDisplayNames`
 * joins those names in AFTER the SQL has chosen its rows (P22's honest
 * omission), so a name filter in the WHERE would be a post-filter over a page.
 * Their column filter is the substitute, and it says what it sees.
 *
 * THREE CLICK TARGETS SHARE ONE CELL and all three guards are load-bearing:
 * the row click navigates, the header click sorts, the funnel does neither
 * (`stopPropagation` inside `ColumnFilter`), and a finished column drag
 * swallows exactly one click (`shouldSuppressClick`).
 */

import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useTableSort } from '../hooks/useTableSort';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnFilters, isColumnFilterActive, type ColumnFilterState, type FilterColumn } from '../hooks/useColumnFilters';
import { useHiddenColumns } from '../hooks/useHiddenColumns';
import { SortableHeader } from './ui/SortableHeader';
import { Button } from './ui/Button';
import { FOCUS_RING_TIGHT } from './ui/focus';
import { ColumnFilter, type FilterValue } from './ui/ColumnFilter';
import { ColumnChooser } from './ui/ColumnChooser';
import { Badge, StatusPill, type Tone } from './ui/Badge';
import { SkeletonRows } from './ui/Skeleton';
import { NameWithCode, HubspotChip } from './ui/EntityLinks';
import { customerState, productState } from '../lib/entity-state';
import {
    AUTHORIZATION_STATUS_LABELS, DUPLICATE_LABELS, ENTITY_STATE_LABELS, NOT_IDENTIFIED,
    REVIEW_REASON_FAMILY_KEYS, REVIEW_REASON_FAMILY_LABELS, REVIEW_REASON_LABELS,
    TEST_DATA_LABEL, TICKET_COLUMN_LABELS, TICKET_STATUS_LABELS, TICKET_STATUSES,
    allReviewReasons, companyLabel, companyTooltip, labelFor,
    ticketStatusGlyph, ticketStatusLabel, ticketStatusTone, ticketStatusTooltip, tooltipFor,
} from '../lib/labels';
import { UNASSIGNED, type MultiFilterKey, type TicketFilters } from '../hooks/useTicketFilters';
import type { SortState } from '../hooks/useTableSort';
import { MatchScoreBadge } from './TicketEntityPanels';
import { downloadCsv, type CsvColumn } from '../lib/csv';
import { isoDate } from '../lib/dates';
import type { Assignee, Ticket } from '../types/domain';

/** The pilot's languages (CLAUDE.md scope) plus the companies it runs in. */
const LANGUAGES = ['DE', 'EN', 'FR', 'IT', 'ES'];
// The funnel and the chip name the company the way the COLUMN does (P38, K4) —
// `dit (Italy)` beside a cell reading `IT` would be two vocabularies for one
// fact, which is the thing `labels.ts` exists to prevent. The ERP code is in the
// tooltip, where it is still reachable and no longer the label.
export const COMPANIES = [
    { code: 'dit', label: companyLabel('dit') },
    { code: 'dch', label: companyLabel('dch') },
];

/**
 * Which query parameter a server-backed column owns. `created` is the one that
 * is not a list — it is a range, so its menu shows two date inputs.
 */
type ServerParam = 'status' | 'companyCode' | 'requestedLanguage' | 'assignedTo' | 'reviewReasonFamily' | 'created';

interface TicketColumn {
    key: string;
    width: number;
    server?: ServerParam;
    /** Client columns only: how the row's value is read for filtering. */
    filterable?: boolean;
}

/**
 * =========================================================================
 * THE WIDTHS ARE A BUDGET, NOT A WISH (P37 — audit S1-02)
 * =========================================================================
 * Measured before this phase: the eight columns of the default set summed to
 * more than the board is wide, `table-layout: auto` then took the CONTENT's
 * word for it (Customer rendered 371 px, Product 458), and `/tickets`
 * overflowed **936 px at 1440 and 1096 px at 1280** — seven of twelve columns
 * entirely off-screen.
 *
 * Three facts make the numbers below what they are:
 *
 *   1. The table is `table-fixed`, so these widths are RATIOS of the board's
 *      width rather than requests the content can overrule. A cell that cannot
 *      fit its value ellipsises it and keeps its `title`; it does not push the
 *      board sideways.
 *   2. `w-full` means a set whose widths sum to LESS than the board simply
 *      stretches, so a sum under the floor costs nothing. Both default sets sum
 *      to **952 px** (`/tickets`) and **964 px** (`/review`, with the two
 *      widths `REVIEW_WIDTHS` moves), against a
 *      measured wrapper width of **975 px at 1280**
 *      and 1135 at 1440 — the slack is deliberate, because the wrapper's own
 *      vertical scrollbar is inside that number and a scrollbar is 15 px on
 *      some machines and 0 on others.
 *   3. The table still carries `minWidth` = the sum of the VISIBLE widths, so a
 *      reader who brings four more columns back through `Columns`, or drags one
 *      wider, gets a horizontal scrollbar rather than nine unreadable columns.
 *
 * A filterable column spends 24 px on padding and 32 px on its funnel before a
 * single character of its heading — which is why the sort chevrons became
 * conditional in `SortableHeader` and why the narrowest headings still
 * truncate. `§17` of `design.md` carries that as a deviation with the
 * arithmetic.
 */
const BASE_COLUMNS: TicketColumn[] = [
    { key: 'ticketNumber', width: 184, filterable: true },
    { key: 'status', width: 148, server: 'status' },
    { key: 'priority', width: 90, filterable: true },
    { key: 'companyCode', width: 88, server: 'companyCode' },
    { key: 'customer', width: 120, filterable: true },
    { key: 'product', width: 112, filterable: true },
    { key: 'requestedDocTypeCode', width: 96, filterable: true },
    { key: 'requestedLanguage', width: 80, server: 'requestedLanguage' },
    { key: 'authorizationStatus', width: 124, filterable: true },
    { key: 'assignedTo', width: 88, server: 'assignedTo' },
    { key: 'dueAt', width: 88, filterable: true },
    { key: 'createdAt', width: 112, server: 'created' },
];

const REVIEW_COLUMN: TicketColumn = { key: 'reviewReason', width: 148, server: 'reviewReasonFamily' };

/**
 * P31 — how long this has been waiting, in whole days, and the review board's
 * default sort. A CLIENT column with `mode="numeric"`: the value is derived from
 * `updatedAt` in the browser (see `idleDays`), so there is no server parameter
 * it could write, and its funnel is the first user of the min/max range P30 built.
 *
 * Only on the review board. `showReviewReason` is what "this is /review" is
 * called in this component — it already decides the review-reason column and its
 * family filter — and the board's own default order is `Created DESC`, which is
 * the right end of a LIST and the wrong end of a QUEUE.
 */
const WAITING_COLUMN: TicketColumn = { key: 'waitingDays', width: 72, filterable: true };

/**
 * Two widths the REVIEW board takes back, because its rows say something else.
 *
 * Measured on the live queue: 24 of its 25 rows carry the status `Needs review`
 * — two short words — where `/tickets` has to hold "Draft waiting for approval",
 * and 15 of 25 carry the same authorization text. The column that was paying for
 * that slack is `What is being asked`, which is the one thing the page exists to
 * show. Same total, moved to where this board's information is.
 */
const REVIEW_WIDTHS: Record<string, number> = { status: 120, authorizationStatus: 120 };

/** Every column either board can show — what `useResizableColumns` and the chooser must see. */
export function allColumns(showReviewReason: boolean): TicketColumn[] {
    return showReviewReason
        ? [...BASE_COLUMNS.slice(0, 2), REVIEW_COLUMN, WAITING_COLUMN, ...BASE_COLUMNS.slice(2)]
            .map(c => (REVIEW_WIDTHS[c.key] ? { ...c, width: REVIEW_WIDTHS[c.key] } : c))
        : BASE_COLUMNS;
}

/**
 * THE FOUR COLUMNS THAT LEAVE EVERY BOARD'S DEFAULT SET (Roman's decision D4).
 *
 * Measured, which is what makes it a decision rather than a preference: Doc
 * Type is `SDS` on 54 of 54 tickets, Priority is `Normal` on 50 of 54, Due says
 * more than Created, and a language is stated on fewer than half. Seven of
 * twelve columns were off-screen at 1440 px while three of the four were on it.
 *
 * It is a DEFAULT and not a removal: `Columns` still lists all twelve, one
 * click brings any of them back, and the choice is remembered per person.
 *
 * `defaultHiddenColumns` stays the per-table addition on top of this — which is
 * why the board passes nothing and `/review` passes only what is true of
 * `/review`.
 */
export const D4_HIDDEN_COLUMNS = ['requestedDocTypeCode', 'priority', 'requestedLanguage', 'createdAt'];

/**
 * The review board's column set: open work needs the queue, not the paperwork.
 *
 * `dueAt` since P31 (a due date is set on 4 of 54); `assignedTo` since P37, on
 * the same measured ground the other three stand on — **all 25 tickets in the
 * live queue are unassigned**, so the column is one repeated word where the
 * board has 60 px to spare per column. Its funnel keeps the `Nobody` value, so
 * "the pile nobody has taken" is still one click away on `/tickets`.
 *
 * Doc Type and Priority are D4's now and would be hidden here anyway; they stay
 * named so that showing them again on `/tickets` cannot silently uncover them
 * here.
 */
export const REVIEW_HIDDEN_COLUMNS = ['requestedDocTypeCode', 'priority', 'dueAt', 'assignedTo'];

/** The review board's default order — waiting longest first (Roman's decision 4). */
export const REVIEW_DEFAULT_SORT = { key: 'waitingDays', dir: 'desc' } as const;

const columnLabel = (key: string) => labelFor(TICKET_COLUMN_LABELS, key);

/**
 * Ticket states that are somebody's open work, and can therefore go stale.
 * Mirrors STALE_CANDIDATE_STATUSES in api/src/agent/steps/stale-tickets.ts.
 */
export const STALE_CANDIDATE_STATUSES = new Set(['NeedsReview', 'AwaitingApproval', 'AwaitingSupplier']);

/**
 * Whole days of quiet, on the SAME clock the ledger runs on (P37a).
 *
 * `lastActivityAt` is the server's `MAX(UpdatedAt, newest user audit row)` —
 * `api/src/shared/ticket-activity.ts` — published precisely so this number
 * cannot disagree with the `TicketStaleReminder` row about the same ticket. It
 * used to be `updatedAt` alone, and on `DRA-2026-00004` that read 33 days for a
 * ticket Bruno had picked up two days earlier; on `DRA-2026-00053` the ledger
 * wrote 22 reminders saying "last touched by agent" after Katia approved its
 * reply. The fallback keeps the old behaviour for any caller that has a ticket
 * from an endpoint which does not attach it.
 */
export function idleDays(t: Ticket, now: number): number {
    const last = new Date(t.lastActivityAt ?? t.updatedAt ?? t.createdAt).getTime();
    return Math.floor((now - last) / 86_400_000);
}

/**
 * Whether the amber "Nd idle" badge shows for this ticket. `staleAfter` is
 * `null` while /agent/status is still loading — nothing is badged rather than
 * badged against a wrong guessed threshold (see the caller for why).
 */
export function showsStaleBadge(t: Ticket, now: number, staleAfter: number | null): boolean {
    return staleAfter !== null && STALE_CANDIDATE_STATUSES.has(t.status) && idleDays(t, now) >= staleAfter;
}

/**
 * The value a client-side column filter groups a row under.
 *
 * Customer and Product bucket every unidentified ticket under one sentinel
 * rather than under the words of its own mail: those words differ on every
 * ticket, so a list of them is a list of rows, not a filter. `NOT_IDENTIFIED`
 * is what makes "show me everything nobody has identified" one click.
 */
export const CLIENT_FILTER_COLUMNS: FilterColumn<Ticket>[] = [
    { key: 'ticketNumber', accessor: t => t.ticketNumber },
    { key: 'priority', accessor: t => t.priority },
    { key: 'customer', accessor: t => (customerState(t) === 'notIdentified' ? NOT_IDENTIFIED : (t.customerName ?? t.customerCode ?? t.customerId ?? '')) },
    { key: 'product', accessor: t => (productState(t) === 'notIdentified' ? NOT_IDENTIFIED : (t.productName ?? t.productCode ?? t.productId ?? '')) },
    { key: 'requestedDocTypeCode', accessor: t => t.requestedDocTypeCode },
    { key: 'authorizationStatus', accessor: t => t.authorizationStatus },
    { key: 'dueAt', accessor: t => (t.dueAt ? isoDate(t.dueAt) : '') },
];

/**
 * The client columns for one render, `now` bound in.
 *
 * `waitingDays` is the only one whose value depends on the clock, and it is
 * `numeric: true` — so its funnel gets the min/max inputs and "waiting more than
 * 7 days" is one filter rather than eleven ticked checkboxes.
 */
export function clientFilterColumns(now: number, showReviewReason: boolean): FilterColumn<Ticket>[] {
    return showReviewReason
        ? [...CLIENT_FILTER_COLUMNS, { key: 'waitingDays', accessor: t => idleDays(t, now), numeric: true }]
        : CLIENT_FILTER_COLUMNS;
}

/**
 * How each column SORTS — one map, shared by the table and by the ticket
 * screen's "Previous / Next in review" (P31). The next ticket in the queue is
 * the next one in the order the reviewer is looking at, and that order is
 * produced here, not guessed there.
 */
export function ticketSortAccessors(now: number): Record<string, (t: Ticket) => string | number | null | undefined> {
    return {
        ticketNumber: t => t.ticketNumber,
        status: t => t.status,
        reviewReason: t => t.reviewReason,
        waitingDays: t => idleDays(t, now),
        priority: t => t.priority,
        companyCode: t => t.companyCode,
        customer: t => t.customerName ?? t.rawCustomerText ?? t.customerId,
        product: t => t.productName ?? t.rawProductText ?? t.productId,
        requestedDocTypeCode: t => t.requestedDocTypeCode,
        requestedLanguage: t => t.requestedLanguage,
        authorizationStatus: t => t.authorizationStatus,
        assignedTo: t => t.assignedTo,
        dueAt: t => t.dueAt,
        createdAt: t => t.createdAt,
    };
}

/**
 * One cell's value in an EXPORT — words, never codes, exactly what the screen
 * shows (Roman's decision 4). Kept beside `Cell` on purpose: a CSV that says
 * `AuthCustomerUnresolved` where the screen says "Who is the customer?" is a
 * different document about the same tickets.
 */
export function csvValue(column: string, t: Ticket, now: number): string {
    switch (column) {
        case 'ticketNumber': return t.ticketNumber;
        // Through the same two helpers the cells use, so a held send reads
        // "Approved, not sent" in the export as well, and the derived reason is
        // in the reason column rather than only on the screen (P37a).
        case 'status': return ticketStatusLabel(t);
        case 'reviewReason': return allReviewReasons(t).map(r => labelFor(REVIEW_REASON_LABELS, r)).join('; ');
        case 'waitingDays': return String(idleDays(t, now));
        case 'priority': return t.priority;
        case 'companyCode': return t.companyCode ?? '';
        case 'customer':
            return customerState(t) === 'notIdentified'
                ? `${ENTITY_STATE_LABELS.notIdentified.label}: ${t.rawCustomerText ?? ''}`.trim()
                : (t.customerName ?? t.customerCode ?? t.customerId ?? '');
        case 'product':
            return productState(t) === 'notIdentified'
                ? `${ENTITY_STATE_LABELS.notIdentified.label}: ${t.rawProductText ?? ''}`.trim()
                : (t.productName ?? t.productCode ?? t.productId ?? '');
        case 'requestedDocTypeCode': return t.requestedDocTypeCode;
        case 'requestedLanguage': return t.requestedLanguage ?? '';
        case 'authorizationStatus': return labelFor(AUTHORIZATION_STATUS_LABELS, t.authorizationStatus);
        case 'assignedTo': return t.assignedTo ?? '';
        // The CSV keeps ISO through the same helper the screen uses, and keeps the
        // RAW company code: a file is read by scripts as well as by people, and
        // `dch` is what joins back to a surrogate id.
        case 'dueAt': return t.dueAt ? isoDate(t.dueAt) : '';
        case 'createdAt': return isoDate(t.createdAt);
        default: return '';
    }
}

/**
 * The review reasons of one ticket, as labelled chips.
 *
 * `max` (P37) caps how many are DRAWN and puts the rest behind a `+N` whose
 * tooltip names them. A ticket carries a `'; '`-joined list and several at once
 * are normal, so on a board row — where the reference allows one line of 44 px
 * — an uncapped list was one of the two things that made a row 141 px tall.
 * The ticket screen passes no `max` and still shows every reason, wrapped:
 * there the question is the subject of the page, not a column of it.
 */
export function ReviewReasonChips({ reasons: all, max }: { reasons: string[]; max?: number }) {
    const shown = max === undefined ? all : all.slice(0, max);
    const rest = all.slice(shown.length);
    if (all.length === 0) {
        return (
            <span
                className="text-xs italic text-muted"
                title="This ticket is in review but carries no reason — a hand status change cleared it. Open it to see what was going on."
            >
                no reason recorded
            </span>
        );
    }
    return (
        <span className={`flex min-w-0 gap-1 ${max === undefined ? 'flex-wrap' : 'items-center whitespace-nowrap'}`}>
            {shown.map(r => (
                <Badge
                    key={r}
                    tone="warn"
                    glyphName="question"
                    title={`${tooltipFor(REVIEW_REASON_LABELS, r)}\n\nCode: ${r}`}
                    className={max === undefined ? '' : 'min-w-0 truncate'}
                >
                    {/* The truncation has to be on the TEXT and not on the badge:
                        a `truncate` on the flex container clips the glyph before
                        the word, and a chip that is a question mark and half a
                        letter says less than an ellipsised sentence. */}
                    <span className={max === undefined ? '' : 'min-w-0 truncate'}>
                        {labelFor(REVIEW_REASON_LABELS, r)}
                    </span>
                </Badge>
            ))}
            {rest.length > 0 && (
                <Badge
                    tone="warn"
                    className="shrink-0"
                    title={`${rest.length} more question${rest.length === 1 ? '' : 's'} on this ticket:\n\n${rest.map(r => `• ${labelFor(REVIEW_REASON_LABELS, r)} (${r})`).join('\n')}`}
                >
                    {/* No glyph on the counter: it summarises chips that each carry
                        one, and a second question mark 4 px away would read as a
                        second question. */}
                    +{rest.length}
                </Badge>
            )}
        </span>
    );
}

export default function TicketTable({
    tickets, staleAfter, showReviewReason, storageKey, emptyMessage,
    filters, setFilters, setOne, sort, onSortChange, columnFilters, setColumnFilter,
    assignees, currentUserEmail, total, defaultHiddenColumns = [],
    defaultSort, hideStatusFilter = false, origin, exportFileName,
    isLoading = false, emptyTotalLabel,
}: {
    tickets: Ticket[];
    staleAfter: number | null;
    showReviewReason: boolean;
    storageKey: string;
    emptyMessage: string;
    filters: TicketFilters;
    setFilters: (next: TicketFilters) => void;
    setOne: <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => void;
    sort: SortState | null;
    onSortChange: (next: SortState) => void;
    columnFilters: Record<string, ColumnFilterState>;
    setColumnFilter: (key: string, state: ColumnFilterState | null) => void;
    assignees: Assignee[];
    /** Marks the reader's own row in the Owner menu — the old "Mine" chip, in its column. */
    currentUserEmail: string | undefined;
    /** The number of tickets that MATCH, which can exceed the number loaded. */
    total: number | undefined;
    /**
     * P38, task 5. The board renders its own toolbar, header band and colgroup
     * while the request is in flight, and skeleton rows in the body — so nothing
     * MOVES when the data lands. Before this the whole table was replaced by the
     * word `Loading…`, which meant the reader watched the page re-lay out.
     */
    isLoading?: boolean;
    /**
     * P38, task 3 — audit S1-03's other half, and reference §12: *"Empty, filters
     * match nothing — says so and gives the total."*
     *
     * The page supplies the sentence because only the page knows what its total
     * COUNTS. `/tickets` has three views and the count belongs to the view; on
     * `/review` it is the queue. Neither can be derived here: `total` is
     * `COUNT(*) OVER()`, a window over the same WHERE, so under a filter it is the
     * filtered number too and cannot answer "how many are there really". That is
     * the same trap the headline fell into, named in `TicketBoard`.
     */
    emptyTotalLabel?: string;
    defaultHiddenColumns?: string[];
    /**
     * The order to use while the URL names none. `/review` passes "waiting
     * longest first"; the board passes nothing and keeps the server's
     * `CreatedAt DESC`. It is a DEFAULT, not a lock - the first header click
     * writes `sort=` into the URL and takes over (P30's controlled sort).
     */
    defaultSort?: SortState;
    /**
     * `/review` shows the Status column but offers no funnel on it (P31, Roman's
     * decision 3). The page IS a selection of statuses; a second control that
     * narrowed it further would be a filter inside a filter, and the one status
     * a reader would reach for - Closed - is the toggle above the table instead.
     */
    hideStatusFilter?: boolean;
    /**
     * Where a row click came FROM, handed to the ticket screen as router state so
     * it can offer "Back to review" and walk the queue. A URL, not a flag: the
     * ticket screen re-reads the exact query and the exact sort from it.
     */
    origin?: string;
    /** Set to offer a CSV of the visible columns, in the order they are shown. */
    exportFileName?: string;
}) {
    const navigate = useNavigate();
    const now = Date.now();

    // ALL columns, hidden ones included — both hooks need the full set.
    // `useResizableColumns` validates stored widths against the keys it knows
    // and drops the rest, so passing only the visible subset would delete a
    // hidden column's width the next time anything persisted (documented in the
    // hook). The chooser needs the full set for the obvious reason: a column
    // missing from the menu that hides it can never come back.
    const columns = useMemo(() => allColumns(showReviewReason), [showReviewReason]);
    // D4 applies to every board; `defaultHiddenColumns` is what is true of THIS
    // one. Merged here rather than at the two call sites, so a third board
    // cannot join with a different idea of the house default.
    const startHidden = useMemo(
        () => [...new Set([...D4_HIDDEN_COLUMNS, ...defaultHiddenColumns])],
        [defaultHiddenColumns],
    );
    // `-hidden-v2` and not `-hidden`: the hook WRITES on mount, so every browser
    // that has opened a board already holds `[]` under the old key — and `[]`
    // cannot be told from "this person chose to show everything". A default that
    // only reaches browsers which have never seen the page is not a default, and
    // the bump is one line against a migration nobody can verify. Widths keep
    // their key: those really are per-person and nothing about them changed.
    const { hidden, toggle: toggleColumn, showAll } = useHiddenColumns(
        columns.map(c => c.key), `${storageKey}-hidden-v2`, startHidden,
    );
    const visible = columns.filter(c => !hidden.includes(c.key));
    const { widthOf, totalWidthOf, startResize, nudgeWidth, resetWidth, resizingKey, shouldSuppressClick } = useResizableColumns(columns, storageKey);

    // `now` moves every render, and both memos are keyed on it deliberately: the
    // day boundary is the only thing that changes these values, and a stale
    // `waitingDays` would sort the queue by yesterday's clock.
    const filterColumns = useMemo(() => clientFilterColumns(now, showReviewReason), [now, showReviewReason]);
    const { filtered, valueCountsFor } = useColumnFilters(tickets, filterColumns, columnFilters);

    const sortAccessors = useMemo(() => ticketSortAccessors(now), [now]);
    // `effectiveSort` is the order the rows are ACTUALLY in — the URL's `sort=`
    // while there is one, and the board's own default until then. P37 needs it
    // because `aria-sort` is a claim about the rows: handing the header the raw
    // `sort` prop would have made `/review` announce "none" on all eight columns
    // while the rows sat in Waiting-descending order, which is P31's default and
    // the reason the page opens at the right end of the queue. `toggleSort` comes
    // from the same hook, so the first click on that column flips the order the
    // reader can see rather than starting from an order nobody was shown.
    const { sorted, sort: effectiveSort, toggleSort } = useTableSort<Ticket>(
        filtered, sortAccessors, defaultSort ?? { key: null, dir: 'asc' }, { sort, onSortChange },
    );

    const truncated = total !== undefined && total > tickets.length;

    // --- the header menus ---------------------------------------------------

    /** Row counts for one server column's canonical values, over the loaded rows. */
    const countsOf = (accessor: (t: Ticket) => string | null): Map<string, number> => {
        const counts = new Map<string, number>();
        for (const t of filtered) {
            const v = accessor(t) ?? UNASSIGNED;
            counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        return counts;
    };

    /**
     * A server column's menu. `selected` is the URL parameter, `values` is the
     * canonical list. Counts are shown only while the column's own filter is
     * OFF — once it is on, the rows carrying the other values are not loaded,
     * and a "0" beside a value that has plenty of tickets would be a lie.
     */
    function serverListFilter(
        key: MultiFilterKey, columnKey: string, values: FilterValue[], counts: Map<string, number> | null,
    ) {
        const current = filters[key];
        const active = current.length > 0;
        return (
            <ColumnFilter
                label={columnLabel(columnKey)}
                // An absent parameter is the ENDPOINT's default, not "everything":
                // no `status` means the open-work statuses, which is not the same
                // as all seven ticked. See ColumnFilter's `emptyMeans`.
                emptyMeans="none"
                active={active}
                values={values.map(v => ({ ...v, count: counts ? (counts.get(v.value) ?? 0) : undefined }))}
                state={active ? { selected: new Set(current) } : undefined}
                onChange={next => setOne(key, next?.selected ? [...next.selected] : [])}
            />
        );
    }

    function filterFor(col: TicketColumn) {
        // See `hideStatusFilter`. `undefined` is what SortableHeader reads as
        // "this header has no funnel" - the column still renders and still sorts.
        if (col.server === 'status' && hideStatusFilter) return undefined;
        switch (col.server) {
            case 'status':
                return serverListFilter('status', col.key,
                    TICKET_STATUSES.map(s => ({
                        value: s,
                        label: labelFor(TICKET_STATUS_LABELS, s),
                        // The funnel filters the STORED status, which is the
                        // honest thing for it to do — and the reason this title
                        // says so for the one status whose row can read
                        // differently since P37a.
                        title: `${tooltipFor(TICKET_STATUS_LABELS, s)}\n\nCode: ${s}`
                            + (s === 'AwaitingApproval'
                                ? '\n\nIncludes the tickets whose row reads "Approved, not sent": the status is '
                                  + 'the same, the send was held. "What is being asked" \u2192 "Approved but not '
                                  + 'sent" selects only those.'
                                : ''),
                    })),
                    filters.status.length > 0 ? null : countsOf(t => t.status));
            case 'companyCode':
                return serverListFilter('companyCode', col.key,
                    COMPANIES.map(c => ({ value: c.code, label: c.label })),
                    filters.companyCode.length > 0 ? null : countsOf(t => t.companyCode));
            case 'requestedLanguage':
                return serverListFilter('requestedLanguage', col.key,
                    LANGUAGES.map(l => ({
                        value: l,
                        label: l,
                        title: `Tickets whose request names ${l}. A ticket that stated no language matches none of these.`,
                    })),
                    filters.requestedLanguage.length > 0 ? null : countsOf(t => t.requestedLanguage));
            case 'reviewReasonFamily':
                return serverListFilter('reviewReasonFamily', col.key,
                    REVIEW_REASON_FAMILY_KEYS.map(f => ({
                        value: f,
                        label: REVIEW_REASON_FAMILY_LABELS[f].label,
                        title: REVIEW_REASON_FAMILY_LABELS[f].tooltip,
                    })),
                    // A ticket can carry reasons from several families, so a
                    // per-family count over rows would not add up to the row
                    // count. Better no number than a number that does not sum.
                    null);
            case 'assignedTo': {
                const counts = filters.assignedTo.length > 0 ? null : countsOf(t => t.assignedTo);
                return serverListFilter('assignedTo', col.key, [
                    { value: UNASSIGNED, label: 'Nobody', title: 'Tickets no one has taken. This is the pile that goes stale.' },
                    ...assignees.map(a => ({
                        value: a.email,
                        label: a.email === currentUserEmail ? `${a.email} (you)` : a.email,
                    })),
                ], counts);
            }
            case 'created':
                return (
                    <ColumnFilter
                        label={columnLabel(col.key)}
                        mode="date"
                        values={[]}
                        active={!!filters.createdFrom || !!filters.createdTo}
                        state={undefined}
                        onChange={() => { /* the date inputs own this menu */ }}
                        dateFrom={filters.createdFrom}
                        dateTo={filters.createdTo}
                        // ONE write for both ends. Two `setOne` calls would each
                        // read the same pre-change `filters`, so the second would
                        // silently undo the first — the query string is the state,
                        // and this component never holds a second copy of it.
                        onDateChange={(from, to) => setFilters({ ...filters, createdFrom: from, createdTo: to })}
                    />
                );
            default: {
                if (!col.filterable) return undefined;
                // The first user of P30's numeric mode: "waiting more than 7 days"
                // is one range, not eleven ticked boxes that go stale at midnight.
                const numeric = filterColumns.find(c => c.key === col.key)?.numeric === true;
                return (
                    <ColumnFilter
                        label={columnLabel(col.key)}
                        mode={numeric ? 'numeric' : 'list'}
                        active={isColumnFilterActive(columnFilters[col.key])}
                        values={valueCountsFor(col.key).map(({ value, count }) => ({
                            value,
                            label: value === NOT_IDENTIFIED
                                ? `(${ENTITY_STATE_LABELS.notIdentified.label.toLowerCase()})`
                                : (value === '' ? '(empty)' : value),
                            title: value === NOT_IDENTIFIED ? ENTITY_STATE_LABELS.notIdentified.tooltip : undefined,
                            count,
                        }))}
                        state={columnFilters[col.key]}
                        onChange={next => setColumnFilter(col.key, next)}
                    />
                );
            }
        }
    }

    /**
     * The CSV is what is on the screen: the VISIBLE columns, in the order they
     * are shown, over the rows after filtering and sorting. Headers and values
     * come from `labels.ts` through `csvValue`, so the file reads like the board
     * and not like the database.
     */
    function exportCsv() {
        const csvColumns: CsvColumn<Ticket>[] = visible.map(col => ({
            header: columnLabel(col.key),
            value: (t: Ticket) => csvValue(col.key, t, now),
        }));
        downloadCsv(`${exportFileName}-${isoDate(now)}.csv`, sorted, csvColumns);
    }

    return (
        // The board fills what is left of the page's own column, so the table box
        // can have a real height and the header can stick to it (see the scrollport
        // comment below). `min-h-0` is what lets `flex-1` shrink below its content
        // — without it a flex item's automatic minimum size is its content, and the
        // box would grow past the viewport exactly as it did before P37.
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                <span>
                    {/* §12: figures are DASHES while loading, never zeros. "0 tickets"
                        is a measurement, and nothing has measured anything yet. */}
                    {isLoading
                        ? '— tickets'
                        : sorted.length === tickets.length
                            ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`
                            : `${sorted.length} of ${tickets.length} loaded ticket${tickets.length === 1 ? '' : 's'} match the column filters`}
                    {!isLoading && truncated && (
                        <span className="ml-1 font-medium text-warn-ink" title="The endpoint returns at most 500 rows. Column filters without a server parameter — Ticket, Customer, Product, Priority, Doc Type, Authorization, Due — can only see what was loaded. Narrow the search, status, company, language, owner or date first.">
                            {' '}— {tickets.length} of {total} loaded; column filters see the loaded rows only
                        </span>
                    )}
                </span>
                <span className="flex items-center gap-2">
                    {exportFileName && (
                        <Button
                            size="sm"
                            disabled={isLoading || sorted.length === 0}
                            onClick={exportCsv}
                            title="Download the visible columns, in the order shown, for the rows below - words, not codes."
                        >
                            <Download className="h-3 w-3" /> Export CSV
                        </Button>
                    )}
                    <ColumnChooser
                        columns={columns.map(c => ({ key: c.key, label: columnLabel(c.key), title: tooltipFor(TICKET_COLUMN_LABELS, c.key) }))}
                        hidden={hidden}
                        onToggle={toggleColumn}
                        onShowAll={showAll}
                    />
                </span>
            </div>

            {/*
              * =================================================================
              * THE SCROLLPORT (P37 — audit S1-08)
              * =================================================================
              * This box used to be `overflow-x-auto` with NO height bound, and
              * that single combination is why no DRA table ever had a sticky
              * header: `overflow-x: auto` computes `overflow-y: auto`, so the box
              * became the nearest scroll container — the sticky ancestor — while
              * measuring `clientHeight === scrollHeight === 2768`. It could not
              * scroll, so nothing ever stuck to it, and the page scrolled
              * underneath in `Layout.tsx`'s `<main>` instead. Measured: at
              * `main.scrollTop = 600` the `<th>` sat at −343 px.
              *
              * `flex-1` + `min-h-0` inside the page's own column (see
              * `TicketBoard` / `ReviewBoard`) gives it a real height, so it
              * scrolls in BOTH directions and the header stays. There is no
              * `calc(100vh - something)` here on purpose: the two boards have
              * different heads, the filter bar changes height as chips are added,
              * and a magic constant would be wrong on one of them from the first
              * day. `min-h-[240px]` is the floor, below which the box gives up
              * and lets the page scroll instead of showing two rows.
              */}
            <div className="mt-2 min-h-[240px] flex-1 overflow-auto rounded-card border border-hair bg-surface">
                {/*
                  * `table-fixed`: the widths in `BASE_COLUMNS` decide, not the
                  * longest customer name in the loaded page. `minWidth` is the sum
                  * of the VISIBLE widths, so the default set fits and a reader who
                  * brings hidden columns back gets a scrollbar rather than nine
                  * columns of ellipsis.
                  */}
                <table
                    className="w-full table-fixed border-collapse text-sm"
                    style={{ minWidth: totalWidthOf(visible.map(c => c.key)) }}
                >
                    {/*
                      * The widths live in a `<colgroup>` and not on the `<td>`s, and
                      * that is not a tidy-up: under `table-layout: fixed` a browser
                      * reads the column widths from the colgroup or from the FIRST
                      * ROW and ignores every later cell. Measured with them still on
                      * the body cells: all eight columns came out at 141.9 px — the
                      * board divided by eight — while `BASE_COLUMNS` said 192/108/72/…
                      */}
                    <colgroup>
                        {visible.map(col => <col key={col.key} style={{ width: widthOf(col.key) }} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            {visible.map(col => (
                                <SortableHeader
                                    key={col.key}
                                    label={columnLabel(col.key)}
                                    sortKey={col.key}
                                    sort={effectiveSort}
                                    onSort={key => {
                                        if (shouldSuppressClick()) return;
                                        toggleSort(key);
                                    }}
                                    onResizeStart={x => startResize(col.key, x)}
                                    onResetWidth={() => resetWidth(col.key)}
                                    onNudgeWidth={delta => nudgeWidth(col.key, delta)}
                                    width={widthOf(col.key)}
                                    isResizing={resizingKey === col.key}
                                    filter={filterFor(col)}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(t => (
                            <tr
                                key={t.id}
                                // No `cursor-pointer` any more (P37, audit S1-01). The
                                // row click stays as a convenience for a mouse, but a
                                // `<tr>` is not a control, cannot be focused and cannot
                                // be pressed — and a pointer that says otherwise is the
                                // screen promising something the keyboard does not get.
                                // The promise is kept by the ticket number, which is a
                                // real `<Link>`.
                                className="border-t border-hair-2 align-middle hover:bg-hover"
                                onClick={() => navigate(`/tickets/${t.ticketNumber}`, origin ? { state: { origin } } : undefined)}
                            >
                                {visible.map(col => (
                                    <td
                                        key={col.key}
                                        // `h-row` (44 px, reference §9) + `truncate` on
                                        // EVERY cell, not on three of them: a row is one
                                        // height because no cell can produce a second
                                        // line, and the cells that used to were the
                                        // status pill (4 lines), the ticket number (3)
                                        // and the review reasons. What a cell cannot show
                                        // it ellipsises and keeps in its `title`.
                                        className={`h-row truncate px-3 ${col.key === 'ticketNumber' ? 'font-medium text-ink' : 'text-body'}`}
                                        title={col.key === 'requestedLanguage' && !t.requestedLanguage ? 'The request did not state a language.' : undefined}
                                    >
                                        <Cell column={col.key} t={t} now={now} staleAfter={staleAfter} origin={origin} board={showReviewReason ? 'review' : undefined} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {isLoading && <SkeletonRows rows={10} columns={visible.length} />}
                        {!isLoading && sorted.length === 0 && (
                            <tr>
                                <td colSpan={visible.length} className="px-3 py-10 text-center text-sm text-muted">
                                    {/*
                                      * P38, task 3. The cell used to say "No ticket matches
                                      * these filters." and stop — which reads exactly like
                                      * "there is no data", on a board with 41 open tickets.
                                      * The headline half of that was fixed in `e338caf`; this
                                      * is the table's own cell, which the audit named
                                      * separately and which is the one a reader is actually
                                      * looking at when the rows are gone.
                                      *
                                      * The total is the PAGE's sentence, not a number formatted
                                      * here — see `emptyTotalLabel`.
                                      */}
                                    <span className="font-medium text-body">{emptyMessage}</span>
                                    {emptyTotalLabel && <span className="ml-1">{emptyTotalLabel}</span>}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * One marker a row can carry beside its ticket number — a hold, a chaser, a
 * sibling, an idle count.
 *
 * =========================================================================
 * WHY ONE IS DRAWN AND THE REST ARE A COUNT (P37)
 * =========================================================================
 * All five used to be rendered inline, and they are what made the ticket
 * number wrap to THREE lines in a 140 px column (audit S2-05). The column is
 * 184 px now and holds a number plus one marker; a second would be clipped by
 * the cell, and a marker a reader cannot see is worse than a marker they are
 * told the count of. So the first by priority is drawn, the rest become `+N`
 * whose tooltip names every one of them.
 *
 * The priority is the order in which the fact changes what a reader should do:
 * a HOLD stops the ticket, a chaser means the customer is waiting on an answer,
 * a sibling link explains where the ticket came from, and an idle count is a
 * measurement of the other four.
 */
interface RowMarker {
    key: string;
    label: string;
    title: string;
    /** Set when the marker is a destination — another ticket. */
    to?: string;
    /**
     * P38: a TONE from the kit's four, never a class pair. These markers used to
     * name `blue-100`, `purple-100`, `indigo-100` twice, `gray-200` and
     * `amber-100` — six families for five facts, of which purple and indigo are
     * in no reference at all.
     *
     * The assignment is §8's one question, who is waiting:
     * - a duplicate is a POINTER at another ticket — `info`, the agent found it;
     * - a chaser is the customer waiting on us — `warn`, a person must answer;
     * - a split link is provenance, true whatever anybody does — `neutral`;
     * - `Test data` is a fact about the ticket's origin — `neutral` (K3);
     * - idle is a MEASUREMENT of the other four and was the third amber on every
     *   `/review` row — `neutral`. It says how long, and how long is a number.
     */
    tone: Tone;
}

export function rowMarkers(t: Ticket, now: number, staleAfter: number | null): RowMarker[] {
    const short = (n: string) => n.replace(/^DRA-\d{4}-/, '');
    const markers: RowMarker[] = [];

    // P27. Another open ticket asks for the same thing. Comes from the server
    // (`siblings.ts`); nothing is derived here.
    if (t.duplicateOf) {
        markers.push({
            key: 'duplicate',
            label: `dup of ${short(t.duplicateOf.ticketNumber)}`,
            title: `${tooltipFor(DUPLICATE_LABELS, t.duplicateOf.linked ? 'duplicateHeld' : 'duplicateFound')}

Other ticket: ${t.duplicateOf.ticketNumber} (${t.duplicateOf.status})`,
            to: `/tickets/${t.duplicateOf.ticketNumber}`,
            tone: 'info',
        });
    }
    // P27. The customer has written again — Bruno's D5, and the one marker that
    // says a person is waiting.
    if ((t.followUpCount ?? 0) > 0) {
        markers.push({
            key: 'chased',
            label: `${labelFor(DUPLICATE_LABELS, 'chased')}${(t.followUpCount ?? 0) > 1 ? ` ${t.followUpCount}×` : ''}`,
            title: tooltipFor(DUPLICATE_LABELS, 'chased'),
            tone: 'warn',
        });
    }
    // P29/P26 — one mail named several articles. Not a duplicate and it must
    // not read like one: nothing is held, the sibling is simply the other half
    // of the same request.
    if (t.splitFrom) {
        markers.push({
            key: 'splitFrom',
            label: `part of ${short(t.splitFrom.ticketNumber)}`,
            title: `One mail asked for several articles. This ticket was raised from ${t.splitFrom.ticketNumber} (${t.splitFrom.status}) and carries one of them. Each sibling has its own purchase check and its own reply.`,
            to: `/tickets/${t.splitFrom.ticketNumber}`,
            tone: 'neutral',
        });
    }
    const into = t.splitInto ?? [];
    if (into.length > 0) {
        markers.push({
            key: 'splitInto',
            label: `+${into.length} split`,
            title: `This request became ${into.length} further ticket(s): ${into.map(c => c.ticketNumber).join(', ')}. Each carries one article and is answered on its own.`,
            tone: 'neutral',
        });
    }
    // P37a, Roman's decision K3. Last of the five by priority on purpose: it
    // changes nothing a reader has to DO about the ticket, it changes what they
    // should conclude if nothing happens — a .example recipient can never receive
    // mail. Katia spent a morning approving a fixture without being told.
    if (t.isTestData) {
        markers.push({
            key: 'testData',
            label: TEST_DATA_LABEL.label,
            title: TEST_DATA_LABEL.tooltip,
            tone: 'neutral',
        });
    }
    if (showsStaleBadge(t, now, staleAfter)) {
        markers.push({
            key: 'idle',
            label: `${idleDays(t, now)}d idle`,
            title: `Nothing has changed on this ticket for ${idleDays(t, now)} days (threshold ${staleAfter}).`,
            tone: 'neutral',
        });
    }
    return markers;
}

function MarkerChip({ marker }: { marker: RowMarker }) {
    /*
     * P38, task 6: these were `text-[10px]`, and the audit measured 70 of them.
     * §3's floor is 12 px and it applies to a marker as much as to a sentence —
     * "dup of 00004" is the one thing on the row that says the ticket is HELD.
     *
     * It costs width, and the width was measured before and after rather than
     * assumed: the Ticket column is 184 px and holds a number (104 px) plus one
     * marker, so the marker absorbs the 2 px per character itself through
     * `truncate` and keeps its full text in the `title`. The NUMBER does not
     * shrink — a clipped marker still says which kind it is, a clipped ticket
     * number is the row losing its name.
     *
     * A marker that LINKS is a control and takes the 32 px floor (inside a 44 px
     * row, so it costs no height); one that only states a fact is a badge and
     * keeps the height of its own words.
     */
    if (marker.to) {
        return (
            <Link
                to={marker.to}
                onClick={e => e.stopPropagation()}
                title={marker.title}
                className={`inline-flex h-8 min-w-0 items-center hover:underline ${FOCUS_RING_TIGHT}`}
            >
                <Badge tone={marker.tone} className="min-w-0">
                    <span className="min-w-0 truncate">{marker.label}</span>
                </Badge>
            </Link>
        );
    }
    return (
        <Badge tone={marker.tone} title={marker.title} className="min-w-0">
            <span className="min-w-0 truncate">{marker.label}</span>
        </Badge>
    );
}

/**
 * One cell. Rendering by column KEY rather than in a fixed sequence of `<td>`s
 * is what lets a column be hidden or (in P31) reordered without the body and
 * the header drifting apart by one column.
 *
 * Every branch produces ONE line. That is the rule the 44 px row rests on, and
 * it is enforced twice — here, by not emitting a second line, and by the `<td>`,
 * which truncates. The two together are why a row cannot be 100 px tall again
 * because somebody added a second fact to a cell.
 */
function Cell({ column, t, now, staleAfter, origin, board }: {
    column: string;
    t: Ticket;
    now: number;
    staleAfter: number | null;
    /** Handed to the ticket number's own link, so the queue survives the click. */
    origin?: string;
    /**
     * P38: `'review'` on `/review` and undefined on `/tickets`. Exactly ONE thing
     * depends on it — the tone of a `NeedsReview` pill — and it is passed rather
     * than derived from `showReviewReason` so that the reason is legible at the
     * call site: it is not "this board shows the reason column", it is "on this
     * board every row needs a person, so amber cannot be the status".
     */
    board?: 'review';
}) {
    switch (column) {
        case 'ticketNumber': {
            const [first, ...rest] = rowMarkers(t, now, staleAfter);
            return (
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {/*
                      * AUDIT S1-01: 41 rows, 0 of which opened their own ticket by
                      * keyboard, while 3 carried a link — to a DIFFERENT ticket. The
                      * number is the link now, it comes first in the tab order, and it
                      * carries the same `origin` router state the row click does, so
                      * "Back to review" and the queue walk survive a keyboard press.
                      *
                      * It is ink and bold rather than `--info`: reference §9 says the
                      * key column is, and a board of 41 blue rows is a board with no
                      * key column. The affordance is the underline on hover and the
                      * focus ring.
                      */}
                    <Link
                        to={`/tickets/${t.ticketNumber}`}
                        state={origin ? { origin } : undefined}
                        onClick={e => e.stopPropagation()}
                        title={`Open ${t.ticketNumber}`}
                        // `h-8` inside a 44 px row: the link is a control, and a
                        // 104 × 16 px one would be 41 new elements under the 32 px
                        // floor P36 spent a phase clearing (reference §4).
                        className={`inline-flex h-8 shrink-0 items-center rounded-ctl hover:underline ${FOCUS_RING_TIGHT}`}
                    >
                        {t.ticketNumber}
                    </Link>
                    {first && <MarkerChip marker={first} />}
                    {rest.length > 0 && (
                        <Badge
                            className="shrink-0"
                            title={`${rest.length} more marker${rest.length === 1 ? '' : 's'} on this ticket:

${rest.map(m => `• ${m.label}`).join('\n')}`}
                        >
                            +{rest.length}
                        </Badge>
                    )}
                </span>
            );
        }
        case 'status': {
            /*
             * P38: the kit's `StatusPill`, which is where §8's four tones and the
             * `whitespace-nowrap` live. P36 built it and deliberately left the
             * boards alone, because the tints were still five stock families in
             * `TICKET_STATUS_BADGE`; that map is now `TICKET_STATUS_TONE` and this
             * is the call that reads it.
             *
             * P37a's word is unchanged and not re-derived here: `ticketStatus*`
             * remain the three functions, and the one pairing that is not a status
             * (`AwaitingApproval` + an approved, held draft = "Approved, not sent")
             * is decided inside them. The CODE in the tooltip stays the stored one,
             * because that is what the database holds and what the funnel filters
             * on.
             *
             * The GLYPH is passed by name and `Badge` decides whether to draw it —
             * only where the tint means something (see `Badge`'s `glyphName`). That
             * is also what keeps P37's arithmetic: the three longest words here are
             * all neutral, so none of them grew.
             */
            const tone = ticketStatusTone(t, board);
            return (
                <StatusPill
                    tone={tone}
                    glyphName={ticketStatusGlyph(t)}
                    title={`${ticketStatusTooltip(t)}\n\nCode: ${t.status}`}
                    className="max-w-full"
                >
                    <span className="min-w-0 truncate">{ticketStatusLabel(t)}</span>
                </StatusPill>
            );
        }
        case 'reviewReason': {
            /*
             * THE BOARD SHOWS EVERY REASON, AND A COLUMN IS NOT A LINE (P38)
             *
             * This cell was briefly changed to `reviewReasonsBesideStatus` — P37a's
             * helper, which drops the one reason the status WORD already states — on
             * the grounds that the board has the same duplication the ticket header
             * had. `p37a:verify` went red on it, and the check was right.
             *
             * The ticket header puts the word and the chip in ONE line, so
             * "Approved, not sent · Approved but not sent" is a sentence saying
             * something twice. The board puts them in two columns with their own
             * headings, and the second one is *What is being asked* — the column a
             * reviewer scans, sorts and FILTERS BY FAMILY. Blanking it for the rows
             * the `send` family selects would make the board contradict the filter
             * that fetched them, which is the one thing P22/P32 forbid.
             *
             * It is the same reason P37a kept the code in `ReviewReasonGuide`: that
             * is the index of open questions, and leaving it out removes the only
             * place that says what to do about it.
             */
            return (
                <span className="flex min-w-0 items-center gap-1 whitespace-nowrap">
                    <ReviewReasonChips reasons={allReviewReasons(t)} max={1} />
                    {/*
                      * P25. The best OPEN suggestion, as a percentage — so a reviewer
                      * scanning the board can tell "one click away" from "nothing
                      * matched" without opening every ticket. The number and its band
                      * are the server's; nothing is computed here.
                      *
                      * P37 dropped the second line that named the candidate. It was
                      * the other half of a 141 px row, and the name is in the tooltip
                      * and on the ticket screen — where the decision is actually made.
                      */}
                    {t.topMatch && (
                        <span
                            className="shrink-0"
                            title={`Closest ${t.topMatch.kind === 'Customer' ? 'customer' : 'article'}: ${t.topMatch.displayName ?? '—'} (${t.topMatch.score}%)`}
                        >
                            <MatchScoreBadge score={t.topMatch.score} band={t.topMatch.band} />
                        </span>
                    )}
                </span>
            );
        }
        case 'waitingDays': {
            const days = idleDays(t, now);
            return (
                <span
                    /*
                     * P38: no longer amber past the threshold. This was the fourth
                     * amber on a `/review` row, and it is the one that could not be
                     * defended: the column exists to be SORTED — it is the board's
                     * default order, longest first — so the rows that matter are
                     * already at the top, and tinting them says a second time what
                     * their position says. Weight and ink, which is what §9 gives a
                     * figure that leads.
                     */
                    className={days >= 7 ? 'font-medium text-ink' : 'text-body'}
                    title={`Nothing has happened on this ticket for ${days} day${days === 1 ? '' : 's'}.`}
                >
                    {days}d
                </span>
            );
        }
        case 'priority':
            return <>{t.priority}</>;
        case 'companyCode':
            // P38, K4: `CH` / `IT`, with the full company name AND the ERP code in
            // the tooltip — `companyTooltip` carries both, so the code a surrogate
            // id is built from is still one hover away. The old cell printed the
            // raw `dch` and its tooltip described the COLUMN rather than the value.
            return t.companyCode
                ? <span title={companyTooltip(t.companyCode)}>{companyLabel(t.companyCode)}</span>
                : <span className="text-muted" title={companyTooltip(null)}>—</span>;
        case 'customer':
            return (
                <span className="flex min-w-0 items-center gap-1">
                    <NameWithCode
                        name={t.customerName}
                        code={t.customerCode}
                        raw={t.rawCustomerText ?? t.customerId}
                        state={customerState(t)}
                        compact
                    />
                    {/* `compact`: the word "HubSpot" cost 61 px of a 140 px column and
                        the chip was 16 px tall. P36 measured 31 of them and handed the
                        size to the phase that sets the row height. */}
                    <HubspotChip url={t.hubspotUrl} compact />
                </span>
            );
        case 'product':
            return (
                <NameWithCode
                    name={t.productName}
                    code={t.productCode}
                    raw={t.rawProductText ?? t.productId}
                    state={productState(t)}
                    compact
                />
            );
        case 'requestedDocTypeCode':
            return <>{t.requestedDocTypeCode}</>;
        case 'requestedLanguage':
            return <>{t.requestedLanguage ?? '—'}</>;
        case 'authorizationStatus':
            return (
                <span title={`${tooltipFor(AUTHORIZATION_STATUS_LABELS, t.authorizationStatus)}\n\nCode: ${t.authorizationStatus}`}>
                    {labelFor(AUTHORIZATION_STATUS_LABELS, t.authorizationStatus)}
                </span>
            );
        case 'assignedTo':
            // The local part, with the address in the `title`. Every DRA account is
            // @dolder.com, so the domain is 11 px of a 108 px column spent on a
            // constant — and the CSV still exports the full address (`csvValue`).
            return t.assignedTo
                ? <span title={t.assignedTo}>{t.assignedTo.replace(/@.*$/, '')}</span>
                : <span className="text-muted" title="Nobody has taken this ticket.">unassigned</span>;
        case 'dueAt':
            return <>{isoDate(t.dueAt)}</>;
        case 'createdAt':
            return <span className="text-muted">{isoDate(t.createdAt)}</span>;
        default:
            return null;
    }
}
